export const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const ms = date - now; // Positive for future dates, negative for past dates
  const isPast = ms < 0;
  const absDiff = Math.abs(ms);

  const sec = Math.floor(absDiff / 1000);
  if (sec <= 60) {
    return isPast ? "just now" : "in a moment";
  }

  const min = Math.floor(sec / 60);
  if (min <= 1) {
    return isPast ? "1 minute ago" : "in 1 minute";
  }
  if (min <= 60) {
    return isPast ? `${min} minutes ago` : `in ${min} minutes`;
  }

  const hr = Math.floor(min / 60);
  if (hr <= 1) {
    return isPast ? "1 hour ago" : "in 1 hour";
  }
  if (hr <= 24) {
    return isPast ? `${hr} hours ago` : `in ${hr} hours`;
  }

  const days = Math.floor(hr / 24);
  if (days <= 1) {
    return isPast ? "yesterday" : "tomorrow";
  }
  if (days <= 7) {
    return isPast ? `${days} days ago` : `in ${days} days`;
  }

  // Handle weeks for the future case
  if (!isPast && days <= 14) {
    return "next week";
  }
  if (!isPast && days <= 30) {
    const weeks = Math.floor(days / 7);
    return `in ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  }

  // For dates far in the past or future, show the formatted date
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString(undefined, options);
}

export const formatPhone = (phoneStr) => {
  if (!phoneStr) return '';
  let v = phoneStr.replace(/[^+0-9]/g, '');
  if (v.startsWith('+1')) {
    v = v.substring(2, 12);
  } else {
    v = v.substring(0, 10);
  }

  if (v.length >= 7) {
    return `(${v.substring(0, 3)}) ${v.substring(3, 6)}-${v.substring(6, 10)}`;
  } else if (v.length >= 4) {
    return `(${v.substring(0, 3)}) ${v.substring(3, 6)}`
  } else if (v.length >= 1) {
    return `(${v.substring(0, 3)}`
  }

  return '';
}

// A contact method's name wherever we show it: "Front Desk (555-123-4567)", or just the number when it
// has no nickname. Dashes instead of formatPhone's area-code parentheses, so the label never nests them.
export const formatPhoneWithNickname = ({ nickname, phoneNumber }) => {
  const formatted = formatPhone(phoneNumber);
  if (!nickname) return formatted;
  return `${nickname} (${formatted.replace('(', '').replace(') ', '-')})`;
}

export const convertPhoneToE164 = (anyPhoneStr) => {
  if (!anyPhoneStr) return null;

  // Remove all non-digit characters
  let digits = anyPhoneStr.replace(/\D/g, '');

  // If it's already E.164 format, return it
  if (digits.startsWith('1') && digits.length === 11) {
    return `+${digits}`;
  }

  // If it's a 10-digit number (assuming U.S.), prepend country code
  if (digits.length === 10) {
    return `+1${digits}`;
  }

  // If it starts with a country code (2-3 digits) and is at least 11 digits, return as is
  if (digits.length >= 11) {
    return `+${digits}`;
  }

  // Invalid format
  throw Error('Invalid phone format')
};

export const getPatientName = (patient) => {
  let name = patient.firstName + ' '

  if (patient.preferredName) {
    name += '(' + patient.preferredName + ') '
  }

  if (patient.middleName) {
    name += patient.middleName + ' '
  }

  name += patient.lastName;

  return name;
}

export const getPatientPicture = (patient) => {
  if (!patient.profilePictureKey) {
    return '/icons/patient.png'
  }

  const publicImages = process.env.VUE_APP_PUBLIC_FILES;
  return `${publicImages}/${patient.profilePictureKey}`;
}

export const getUserPicture = (user) => {
  if (!user.profilePictureKey) {
    return '/icons/patient.png'
  }

  const publicImages = process.env.VUE_APP_PUBLIC_FILES;
  return `${publicImages}/${user.profilePictureKey}`;
}

export const getAccountLogo = (accountLogoKey) => {
  if (!accountLogoKey) return null;

  // Check if it's already a blob URL (temporary preview)
  if (accountLogoKey.startsWith('blob:')) {
    return accountLogoKey;
  }

  // Construct URL from key using PUBLIC_FILES container
  const publicFiles = process.env.VUE_APP_PUBLIC_FILES;
  return `${publicFiles}/${accountLogoKey}`;
}

export const getTimeOfDay = (dateStr) => {
  const date = new Date(dateStr);

  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true // Use true for AM/PM format, false for 24-hour format
  });

  return time;
}

export const formatDateShort = (dateStr) => {
  // Parse the given date string
  const dt = new Date(dateStr);
  if (isNaN(dt)) {
    return 'Invalid Date';
  }

  const now = new Date();

  // Is it today?
  if (
    dt.getFullYear() === now.getFullYear() &&
    dt.getMonth() === now.getMonth() &&
    dt.getDate() === now.getDate()
  ) {
    return dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  // Is it within 7 days?
  if (dt < now) {
    const diffDays = (now - dt) / (1000 * 60 * 60 * 24);
    if (diffDays < 7) {
      return dt.toLocaleDateString(undefined, { weekday: 'short' });
    }
  }

  // For any other case (either older than a week or a future date), return "MMM D" (e.g. "Jun 4")
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '-'
  const dt = new Date(dateStr)
  if (isNaN(dt)) return '-'
  const now = new Date()
  const diffMs = now - dt
  if (diffMs < 0) return 'just now'
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  const years = Math.floor(months / 12)
  return `${years}y ago`
}

export const formatFutureDateShort = (dateStr) => {
  // Parse the given date string
  const dt = new Date(dateStr);
  if (isNaN(dt)) {
    return 'Invalid Date';
  }

  const now = new Date();

  if (dt > now) {
    // Calculate the difference in days (ms difference converted to days)
    const diffDays = (dt - now) / (1000 * 60 * 60 * 24);
    if (diffDays < 7) {
      // Return the short name of the weekday (e.g., "Sat")
      return dt.toLocaleDateString(undefined, { weekday: 'short' });
    }
  }

  // For any other case (either more than a week away or a past date), return "MMM D" (e.g. "Jun 4")
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export const detectQueryType = (query) => {
  if (!query || query.trim() === '') {
    return { type: 'empty', value: '' };
  }

  const trimmedQuery = query.trim();

  // Email detection - contains @ and has at least one character before and after @
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (emailRegex.test(trimmedQuery) || trimmedQuery.includes('@')) {
    return { type: 'email', value: trimmedQuery };
  }

  // Phone number detection - allows digits, +, (), spaces, -, .
  const phoneRegex = /^[+()\d\s.-]{3,}$/;
  const digitCount = (trimmedQuery.match(/\d/g) || []).length;
  const totalLength = trimmedQuery.length;

  if (phoneRegex.test(trimmedQuery) && digitCount / totalLength > 0.6) {
    // Format the phone number for searching
    const formattedPhone = formatPhoneForSearch(trimmedQuery);
    return { type: 'phone', value: formattedPhone };
  }

  // Default to name search
  return { type: 'name', value: trimmedQuery };
};

export const formatPhoneForSearch = (phoneStr) => {
  if (!phoneStr) return '';

  // Remove all non-digit characters except + at the beginning
  let cleaned = phoneStr.replace(/[^0-9+]/g, '');

  // Remove + from anywhere except the beginning
  if (cleaned.startsWith('+')) {
    cleaned = '+' + cleaned.slice(1).replace(/\+/g, '');
  } else {
    cleaned = cleaned.replace(/\+/g, '');
  }

  // If it starts with +1, remove the country code for US numbers
  if (cleaned.startsWith('+1')) {
    cleaned = cleaned.slice(2);
  }

  // If it starts with 1 and has 11 digits total, remove the leading 1
  if (cleaned.startsWith('1') && cleaned.length === 11) {
    cleaned = cleaned.slice(1);
  }

  return cleaned;
};